import { Calendar, Clock, Globe, Mail, Video, Leaf } from 'lucide-react';

const EVENT_DATE = 'Thursday, 9 July 2026';
const EVENT_TIME_EST = '10:00 AM – 11:30 AM Eastern';
const EVENT_TIME_UK = '3:00 PM – 4:30 PM UK & Europe';

const nextSteps = [
  {
    icon: <Mail size={17} className="text-sage-400 flex-shrink-0 mt-0.5" />,
    text: "A confirmation email with your live join link is on its way — it should arrive in your inbox in the next few minutes if it hasn't already.",
  },
  {
    icon: <Video size={17} className="text-sage-400 flex-shrink-0 mt-0.5" />,
    text: 'You can also join directly on the day using this link:',
    link: { href: 'https://my.nlpos.app/live', label: 'app.humanpotentialos.com/live' },
  },
  {
    icon: <Clock size={17} className="text-sage-400 flex-shrink-0 mt-0.5" />,
    text: "Add the training to your calendar so you don't miss it. It starts at 10:00 AM Eastern / 3:00 PM UK & Europe.",
  },
  {
    icon: <Leaf size={17} className="text-sage-400 flex-shrink-0 mt-0.5" />,
    text: 'Come ready to think, not just listen. This training is designed to shift something for you — not just give you more information.',
  },
];

function GreenCheck() {
  return (
    <span className="w-5 h-5 rounded-full bg-sage-700 flex items-center justify-center flex-shrink-0">
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

export function CoachingPlateauThankYouPage() {
  return (
    <div className="min-h-screen bg-chocolate-900 font-body flex items-center justify-center px-5 py-20">
      <div className="max-w-xl w-full">

        {/* Check circle */}
        <div className="flex justify-center mb-7">
          <div
            className="w-16 h-16 rounded-full bg-sage-100 border-2 border-sage-400 flex items-center justify-center"
          >
            <svg width="24" height="20" viewBox="0 0 24 20" fill="none">
              <path d="M2 10L8.5 16.5L22 2" stroke="#446A39" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        {/* Badge */}
        <div className="flex justify-center mb-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-sage-700 bg-sage-900/20 text-sage-400 text-xs font-semibold tracking-wide uppercase">
            <Leaf size={12} />
            The Coaching Plateau — Free Live Training
          </div>
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl font-extrabold text-white text-center mb-4 tracking-tight">
          You're In.
        </h1>

        {/* Subtext */}
        <p className="text-sm text-chocolate-200 text-center leading-relaxed mb-8">
          Your seat is reserved for{' '}
          <span className="text-white font-semibold">The Coaching Plateau</span>{' '}
          on <span className="text-white font-semibold">{EVENT_DATE}</span>.{' '}
          Duration: 90 minutes + Q&amp;A.
        </p>

        {/* Event details strip */}
        <div className="flex flex-col sm:flex-row gap-2 justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-chocolate-800 border border-chocolate-700 text-white text-sm">
            <Calendar size={14} className="text-sage-400 flex-shrink-0" />
            {EVENT_DATE}
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-chocolate-800 border border-chocolate-700 text-white text-sm">
            <Clock size={14} className="text-sage-400 flex-shrink-0" />
            {EVENT_TIME_EST}
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-chocolate-800 border border-chocolate-700 text-white text-sm">
            <Globe size={14} className="text-sage-400 flex-shrink-0" />
            {EVENT_TIME_UK}
          </div>
        </div>

        {/* What happens next */}
        <div className="rounded-xl bg-chocolate-800 border border-chocolate-700 px-6 py-6 mb-4">
          <p className="text-white font-semibold text-sm mb-5">Here's what happens next:</p>
          <ul className="space-y-4">
            {nextSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                {step.icon}
                <p className="text-sm text-chocolate-200 leading-relaxed">
                  {step.text}
                  {step.link && (
                    <>
                      {' '}
                      <a
                        href={step.link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sage-400 hover:text-sage-300 underline underline-offset-2 font-medium transition-colors"
                      >
                        {step.link.label}
                      </a>
                    </>
                  )}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* Patrick sign-off */}
        <div className="rounded-xl bg-chocolate-800 border border-chocolate-700 px-6 py-5">
          <div className="flex items-center gap-4">
            <img
              src="/Patrick_Profile_Under_2MB_q95.jpg"
              alt="Patrick Dahdal"
              className="w-11 h-11 rounded-full object-cover border-2 border-sage-600/40 flex-shrink-0"
            />
            <div>
              <p className="text-chocolate-300 text-sm mb-0.5">See you on Thursday 9th July.</p>
              <p className="text-white text-sm font-semibold">Patrick</p>
              <p className="text-chocolate-400 text-xs">Founder, NLP OS™</p>
            </div>
          </div>
        </div>

        {/* What to expect teaser */}
        <div className="mt-8 pt-8 border-t border-chocolate-800">
          <p className="text-xs text-chocolate-400 text-center mb-4 uppercase tracking-wide font-semibold">What you'll walk away with</p>
          <ul className="space-y-2.5">
            {[
              'Clarity on the actual condition underneath your plateau',
              'A framework you can use immediately — not after more learning',
              'The ability to ask the right question before taking the next action',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <GreenCheck />
                <span className="text-sm text-chocolate-200 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>

      </div>
    </div>
  );
}
